import React from "react";
import { Box, Skeleton, Stack, Tooltip, Typography, tooltipClasses } from "@mui/material";
import { styled } from "@mui/material/styles";

const DEFAULT_SCALE = [0, 25, 50, 75, 100];
const IDENTITY = (v) => v;

const clamp = (n) => Math.max(0, Math.min(100, n));

const HyroBenchmarkBar = ({
  value = 0,
  markers = [],
  scale = DEFAULT_SCALE,
  showScaleLabels = true,
  loading = false,
  currentLabel,
  formatValue = IDENTITY
}) => {
  if (loading) {
    return (
      <Stack gap={0.25} width="100%">
        <Skeleton variant="rectangular" width="100%" height={12} sx={{ borderRadius: 0.25 }} />
        {showScaleLabels && <Skeleton variant="text" width="100%" />}
      </Stack>
    );
  }

  const labeledMarkers = markers.filter((m) => m.label);
  const hasTooltip = !!currentLabel || labeledMarkers.length > 0;

  const tooltipContent = hasTooltip ? (
    <TooltipContent>
      {currentLabel && (
        <TooltipRow>
          <TooltipLegend>
            <FillSwatch circle />
            <TooltipLabel>{currentLabel}</TooltipLabel>
          </TooltipLegend>
          <TooltipValue>{formatValue(value)}</TooltipValue>
        </TooltipRow>
      )}
      {labeledMarkers.map((marker, i) => (
        <TooltipRow key={`tooltip-marker-${i}`}>
          <TooltipLegend>
            <MarkerSwatch style={{ backgroundColor: marker.color }} />
            <TooltipLabel>{marker.label}</TooltipLabel>
          </TooltipLegend>
          <TooltipValue>{formatValue(marker.value)}</TooltipValue>
        </TooltipRow>
      ))}
    </TooltipContent>
  ) : null;

  const bar = (
    <Track>
      <Fill style={{ width: `${clamp(value)}%` }} />
      {markers.map((marker, i) => (
        <Marker
          key={`marker-${i}`}
          style={{ left: `${clamp(marker.value)}%`, backgroundColor: marker.color }}
        />
      ))}
    </Track>
  );

  return (
    <Stack gap={0.25} width="100%">
      {hasTooltip ? (
        <StyledTooltip title={tooltipContent} placement="top" arrow={false}>
          {bar}
        </StyledTooltip>
      ) : bar}
      {showScaleLabels && (
        <ScaleRow>
          {scale.map((tick) => (
            <ScaleLabel key={tick}>{tick}{tick === 0 ? "" : "%"}</ScaleLabel>
          ))}
        </ScaleRow>
      )}
    </Stack>
  );
};

const Track = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  height: 12,
  borderRadius: 2,
  backgroundColor: theme.palette.grey[200],
  overflow: "hidden"
}));

const Fill = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  height: "100%",
  backgroundColor: theme.palette.secondary[400]
}));

const Marker = styled(Box)({
  position: "absolute",
  top: 0,
  height: "100%",
  width: 2,
  transform: "translateX(-1px)"
});

const ScaleRow = styled(Stack)({
  flexDirection: "row",
  justifyContent: "space-between",
  width: "100%"
});

const ScaleLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text[700],
  fontSize: 8,
  lineHeight: 1.4
}));

const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.system[0],
    color: theme.palette.text[800],
    padding: theme.spacing(1.5, 1.75),
    borderRadius: 4,
    boxShadow: "0px 3px 1px -2px rgba(59,83,105,0.2), 0px 2px 2px 0px rgba(59,83,105,0.14), 0px 1px 5px 0px rgba(59,83,105,0.12)",
    maxWidth: "none"
  }
}));

const TooltipContent = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(1),
  minWidth: 130
}));

const TooltipRow = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  width: "100%"
}));

const TooltipLegend = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  alignItems: "center",
  gap: theme.spacing(1)
}));

const FillSwatch = styled(Box, {
  shouldForwardProp: (prop) => prop !== "circle"
})(({ theme, circle }) => ({
  width: 14,
  height: 14,
  borderRadius: 2,
  backgroundColor: theme.palette.secondary[400],
  ...(circle ? { borderRadius: "50%" } : {})
}));

const MarkerSwatch = styled(Box)({
  width: 2,
  height: 12,
  marginLeft: 6,
  marginRight: 6
});

const TooltipLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text[800],
  fontSize: 12,
  lineHeight: "18px",
  fontWeight: 500,
  letterSpacing: "0.16px"
}));

const TooltipValue = styled(Typography)(({ theme }) => ({
  color: theme.palette.text[700],
  fontSize: 12,
  lineHeight: "18px",
  fontWeight: 500,
  letterSpacing: "0.16px"
}));

export default HyroBenchmarkBar;
